import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import GetMcContainer from '@/components/common/get-mc-container/get-mc-container';
import ShowQrDialog from '@/components/common/show-qr-dialog/show-qr-dialog';
import AccordionItem from '@/components/navigation/navigation-items/accordion-item';
import AccordionItemSkeleton from '@/components/navigation/navigation-items/accordion-item-skeleton';
import CreatePlaceDialog from '@/components/place/create-place-dialog';
import EditPlaceDialog from '@/components/place/edit-place-dialog';
import {
    AUDITS_ROUTE,
    AVATARS,
    BIG_RESOURCE_RESET_DEMO,
    CONTRACTS_ROUTE,
    DEVIS_ROUTE,
    DISORDERS_TREATMENT_ROUTE,
    INTERVENTIONS_ROUTE,
    NAVIGATION_ROUTE,
    PLACES,
    PLACES_METADATA_AVATAR_TYPE_NAME,
    ROLE_BIG_READ_AUDIT,
    ROLE_BIG_READ_CONTRACT,
    ROLE_BIG_DELETE_PLACE,
    ROLE_BIG_READ_DEMAND,
    ROLE_BIG_READ_INTERVENTION,
    ROLE_BIG_READ_QUOTE,
    ROLE_BIG_UPDATE_PLACE,
} from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import { Avatar } from '@/utils/types';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { List, ListItemButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { map, nth, range, split } from 'lodash';
import { FilterContext, InfinitePagination } from 'ra-ui-materialui';
import { forwardRef, Fragment, useImperativeHandle, useMemo, useState } from 'react';
import {
    FilterForm,
    InfiniteListBase,
    TextInput,
    useCreate,
    useDelete,
    useNotify,
    usePermissions,
    useRefresh,
    useTranslate,
    useUnselectAll,
    WithListContext,
} from 'react-admin';
import { useLocation, useNavigate, useParams } from 'react-router';
import { v4 as uuid } from 'uuid';

const optionsList = (permissions: string[] | undefined) => [
    {
        label: 'main.navigation.disorder',
        routeTo: DISORDERS_TREATMENT_ROUTE,
        disabled: !permissions?.includes(ROLE_BIG_READ_DEMAND),
    },
    {
        label: 'main.navigation.intervention',
        routeTo: INTERVENTIONS_ROUTE,
        disabled: !permissions?.includes(ROLE_BIG_READ_INTERVENTION),
    },
    {
        label: 'main.navigation.audit',
        routeTo: AUDITS_ROUTE,
        disabled: !permissions?.includes(ROLE_BIG_READ_AUDIT),
    },
    {
        label: 'main.navigation.contract',
        routeTo: CONTRACTS_ROUTE,
        disabled: !permissions?.includes(ROLE_BIG_READ_CONTRACT),
    },
    {
        label: 'main.navigation.devis',
        routeTo: DEVIS_ROUTE,
        disabled: !permissions?.includes(ROLE_BIG_READ_QUOTE),
    },
];

interface Props {
    siteUriId?: string;
    clientUriId?: string;
}

interface PlacesListHandle {
    openCreateDialog: (siteId: string) => void;
}

const PlacesList = forwardRef<PlacesListHandle, Props>(({ siteUriId, clientUriId }, ref) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [deleteOne, { isLoading: isLoadingDelete }] = useDelete();
    const notify = useNotify();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<string>('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedPlaceId, setSelectedPlaceId] = useState<string>('');
    const [selectedSiteId, setSelectedSiteId] = useState<string>('');
    const unselectAll = useUnselectAll(AVATARS);
    const { permissions } = usePermissions();
    const [create] = useCreate();
    const refresh = useRefresh();

    const { placeId } = useParams();

    const location = useLocation();

    useImperativeHandle(ref, () => ({
        openCreateDialog: (siteId: string) => {
            setSelectedSiteId(siteId);
            setOpenCreateDialog(true);
        },
    }));

    // Verificar si estamos en una ruta de recurso dentro del place
    const isInResourceRoute = useMemo(() => {
        const pathParts = split(location.pathname, '/');
        const resourceRoutes = [
            INTERVENTIONS_ROUTE,
            CONTRACTS_ROUTE,
            AUDITS_ROUTE,
            DISORDERS_TREATMENT_ROUTE,
            DEVIS_ROUTE,
        ];
        // Verificar si el pathname contiene algún recurso después de /place/:placeId
        const placeIndex = pathParts.indexOf('place');
        if (placeIndex !== -1 && placeIndex < pathParts.length - 1) {
            const afterPlace = pathParts.slice(placeIndex + 2); // +2 para saltar 'place' y el placeId
            return resourceRoutes.some((route) => afterPlace.includes(route));
        }
        return false;
    }, [location.pathname]);
    const [showQrDialogState, setShowQrDialogState] = useState<boolean>(false);
    const [qrDialogInfo, setQrDialogInfo] = useState<any>(undefined);

    const navigateTo = (url: string, placeIdParam: string) => {
        const siteIdParam = extractIdFromURI(siteUriId ?? '');
        const clientIdParam = extractIdFromURI(clientUriId ?? '');
        navigate(`/${NAVIGATION_ROUTE}/client/${clientIdParam}/site/${siteIdParam}/place/${placeIdParam}/${url}`);
    };

    const navigateToEdit = (placeId: string) => {
        setSelectedPlaceId(placeId);
        setSelectedSiteId(extractIdFromURI(siteUriId ?? '') ?? '');
        setOpenEditDialog(true);
    };

    const handleSelect = (id: string) => {
        const siteIdParam = extractIdFromURI(siteUriId ?? '');
        const clientIdParam = extractIdFromURI(clientUriId ?? '');
        navigate(`client/${clientIdParam}/site/${siteIdParam}/place/${id}`);
    };

    const handleSimulateApp = (mc: any) => {
        window.open(mc.yourls.mcUrl, '_blank');
    };

    const handleShowQrDialogOpen = (name: string, url: string) => {
        setQrDialogInfo({
            name,
            url,
        });
        setShowQrDialogState(true);
    };

    const handleShowQrDialogClose = () => {
        setShowQrDialogState(false);
        setQrDialogInfo(undefined);
    };

    const handleResetDemo = (placeAvatarId: string) => {
        create(
            BIG_RESOURCE_RESET_DEMO,
            {
                data: {
                    site: placeAvatarId,
                    place: placeAvatarId,
                },
            },
            {
                onSuccess: () => {
                    refresh();
                    const clientIdParam = extractIdFromURI(clientUriId ?? '') ?? '';
                    const siteIdParam = extractIdFromURI(siteUriId ?? '') ?? '';
                    const placeIdParam = extractIdFromURI(placeAvatarId) ?? '';
                    navigate(
                        `/${NAVIGATION_ROUTE}/client/${clientIdParam}/site/${siteIdParam}/place/${placeIdParam}`,
                    );
                },
            },
        );
    };

    const handleRemoveAvatar = (avatarUriId: string) => {
        if (!!deleteId)
            deleteOne(
                `${PLACES}_${extractIdFromURI(siteUriId)}`,
                {
                    id: avatarUriId,
                    meta: {
                        requestType: 'update',
                        id: `${avatarUriId}/enable`,
                        data: {
                            enabled: false,
                            children: true,
                        },
                        realResource: AVATARS,
                    },
                },
                {
                    mutationMode: 'optimistic',
                    onSuccess: () => {
                        notify('ra.notification.deleted', {
                            type: 'success',
                            messageArgs: { smart_count: 1 },
                        });
                        setOpenDeleteModal(false);
                        setDeleteId('');
                    },
                },
            );
    };

    const handleNavigateWithRemoveSelectedAvatar = (url: string, id: string) => {
        unselectAll();
        navigateTo(url, extractIdFromURI(id) ?? '');
    };

    return (
        <Box>
            <InfiniteListBase
                resource={`${PLACES}_${extractIdFromURI(siteUriId)}`}
                perPage={30}
                sort={{
                    field: 'creationDate',
                    order: 'ASC',
                }}
                filter={{
                    'metadataAvatarType.name': PLACES_METADATA_AVATAR_TYPE_NAME,
                    ...(siteUriId && {
                        parent: siteUriId,
                    }),
                    enabled: true,
                }}
                storeKey={`placeList-${uuid()}`}
                disableSyncWithLocation
                queryOptions={{
                    enabled: true,
                    meta: {
                        realResource: AVATARS,
                    },
                }}
            >
                <WithListContext
                    render={({ data, isLoading, total }) => (
                        <Fragment>
                            {!siteUriId && (
                                <Box
                                    sx={{
                                        '& .RaFilterFormInput-spacer': {
                                            display: 'none',
                                        },
                                        '& form': {
                                            minHeight: 0,
                                        },
                                        '& form div': {
                                            width: '100%',
                                        },
                                        '& form .RaFilterForm-clearFix': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <FilterContext.Provider
                                        value={[
                                            <TextInput
                                                name='name'
                                                placeholder={translate('main.searchPlace')}
                                                label={false}
                                                source='name'
                                                helperText={false}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        borderRadius: 0,
                                                    },
                                                    mt: '0 !important',
                                                }}
                                                alwaysOn
                                            />,
                                        ]}
                                    >
                                        <FilterForm />
                                    </FilterContext.Provider>
                                </Box>
                            )}

                            {data && data.length === 0 && (
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        textAlign: 'center',
                                        color: (theme) => theme.palette.action.disabled,
                                        py: 1,
                                    }}
                                >
                                    {translate('place.no_items')}
                                </Typography>
                            )}

                            {map(data, ({ id, name, mcs, customFields }: Avatar) => (
                                <GetMcContainer mcUriId={nth(mcs, 0)} key={id}>
                                    {({ mc, isLoading: isLoadingMc }: any) => (
                                        <AccordionItem
                                            id={id}
                                            name={name}
                                            {...(siteUriId && {
                                                pl: 6,
                                            })}
                                            onSelect={() => handleSelect(extractIdFromURI(id) ?? '')}
                                            selected={extractIdFromURI(id) === placeId && !isInResourceRoute}
                                            defaultExpanded={extractIdFromURI(id) === placeId}
                                            menuItems={[
                                                {
                                                    label: translate('ra.action.edit'),
                                                    icon: <EditOutlined />,
                                                    onClick: () =>
                                                        navigateToEdit(extractIdFromURI(id) as string),
                                                    disabled: !permissions?.includes(ROLE_BIG_UPDATE_PLACE),
                                                },
                                                {
                                                    label: translate('ra.action.delete'),
                                                    icon: <DeleteOutlined />,
                                                    onClick: () => {
                                                        setOpenDeleteModal(true);
                                                        setDeleteId(id);
                                                    },
                                                    disabled: !permissions?.includes(ROLE_BIG_DELETE_PLACE),
                                                },
                                                ...(!isLoadingMc && mc
                                                    ? [
                                                          {
                                                              label: translate('main.navigation.open_web_app'),
                                                              icon: <OpenInNewIcon />,
                                                              onClick: () => {
                                                                  handleSimulateApp(mc);
                                                              },
                                                          },
                                                      ]
                                                    : []),
                                                {
                                                    label: translate('main.navigation.qr_code'),
                                                    icon: <QrCodeIcon />,
                                                    onClick: () => handleShowQrDialogOpen(name, mc?.yourls?.mcUrl),
                                                },
                                                ...(siteUriId &&
                                                clientUriId &&
                                                customFields?.demo
                                                    ? [
                                                          {
                                                              label: translate('main.navigation.reset_demo'),
                                                              icon: <RestartAltIcon />,
                                                              onClick: () => handleResetDemo(id),
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                        >
                                            <List disablePadding>
                                                {map(optionsList(permissions), ({ label, routeTo, disabled }, index) => (
                                                    <ListItemButton
                                                        onClick={() =>
                                                            handleNavigateWithRemoveSelectedAvatar(
                                                                routeTo,
                                                                extractIdFromURI(id) ?? '',
                                                            )
                                                        }
                                                        disabled={disabled}
                                                        disableGutters
                                                        sx={{
                                                            p: 0,
                                                        }}
                                                        key={index}
                                                        selected={
                                                            extractIdFromURI(id) === placeId &&
                                                            (() => {
                                                                const pathParts = split(location.pathname, '/');
                                                                const placeIndex = pathParts.indexOf('place');
                                                                // El routeTo debería estar después de 'place' y el placeId (índice placeIndex + 2)
                                                                // Ahora sin el placeId duplicado al final
                                                                return (
                                                                    placeIndex !== -1 &&
                                                                    placeIndex + 2 < pathParts.length &&
                                                                    pathParts[placeIndex + 2] === routeTo
                                                                );
                                                            })()
                                                        }
                                                    >
                                                        <ListItemButton
                                                            sx={{
                                                                ...(siteUriId
                                                                    ? {
                                                                          pl: 12,
                                                                      }
                                                                    : {
                                                                          pl: 3.5,
                                                                      }),
                                                            }}
                                                        >
                                                            <Typography fontSize={14}>{translate(label)}</Typography>
                                                        </ListItemButton>
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </AccordionItem>
                                    )}
                                </GetMcContainer>
                            ))}

                            {isLoading && range(3).map((index) => <AccordionItemSkeleton key={index} />)}

                            {data && total !== undefined && data.length < total && <InfinitePagination />}

                            {openDeleteModal && (
                                <DeleteDialog
                                    isLoading={isLoadingDelete}
                                    onClose={() => setOpenDeleteModal(false)}
                                    onClick={() => handleRemoveAvatar(deleteId)}
                                />
                            )}

                            {qrDialogInfo !== undefined && showQrDialogState && (
                                <ShowQrDialog
                                    open={showQrDialogState}
                                    onClose={handleShowQrDialogClose}
                                    url={qrDialogInfo.url}
                                    downloadName={qrDialogInfo.name}
                                />
                            )}
                        </Fragment>
                    )}
                />
            </InfiniteListBase>

            {openCreateDialog && selectedSiteId && (
                <CreatePlaceDialog
                    open={openCreateDialog}
                    onClose={() => {
                        setOpenCreateDialog(false);
                        setSelectedSiteId('');
                    }}
                    siteId={selectedSiteId}
                />
            )}

            {openEditDialog && selectedPlaceId && selectedSiteId && (
                <EditPlaceDialog
                    open={openEditDialog}
                    onClose={() => {
                        setOpenEditDialog(false);
                        setSelectedPlaceId('');
                        setSelectedSiteId('');
                    }}
                    placeId={selectedPlaceId}
                    siteId={selectedSiteId}
                />
            )}
        </Box>
    );
});

PlacesList.displayName = 'PlacesList';

export default PlacesList;
